import React, { createContext, useContext, useReducer, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const languageReducer = (state, action) => {
  switch (action?.type) {
    case 'SET_LANGUAGE':
      return {
        ...state,
        currentLanguage: action?.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action?.payload,
      };
    default:
      return state;
  }
};

const initialState = {
  currentLanguage: 'en',
  isLoading: false,
  supportedLanguages: [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ],
};

export const LanguageProvider = ({ children }) => {
  const [state, dispatch] = useReducer(languageReducer, initialState);

  // Load saved language on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('dinner-memory-language');
    if (savedLanguage && state?.supportedLanguages?.some(lang => lang?.code === savedLanguage)) {
      dispatch({ type: 'SET_LANGUAGE', payload: savedLanguage });
    } else {
      // Detect browser language
      const browserLanguage = navigator.language?.split('-')?.[0] || 'en';
      const supportedLanguage = state?.supportedLanguages?.find(lang => lang?.code === browserLanguage);
      if (supportedLanguage) {
        dispatch({ type: 'SET_LANGUAGE', payload: browserLanguage });
      }
    }
  }, []);

  const changeLanguage = async (languageCode) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Save to localStorage
      localStorage.setItem('dinner-memory-language', languageCode);
      
      // Simulate loading time for language resources
      await new Promise(resolve => setTimeout(resolve, 300));
      
      dispatch({ type: 'SET_LANGUAGE', payload: languageCode });
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const getCurrentLanguageData = () => {
    return state?.supportedLanguages?.find(lang => lang?.code === state?.currentLanguage) || state?.supportedLanguages?.[0];
  };

  const value = {
    ...state,
    changeLanguage,
    getCurrentLanguageData,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};